import { getLastThirtyDaysRange, TrafficSourceCodeEnum } from '@metapic-apps/shared'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { localStorageService } from '@/lib/services/localStorage.services'

export type GlobalDateRange = {
  startDate: string
  endDate: string
}
type SavedGlobalFilter = {
  trafficSource?: TrafficSourceCodeEnum
  dateRange?: GlobalDateRange
}
const STORAGE_KEY = 'advertiser_global_filter'
const TRAFFIC_SOURCE_VALUES = Object.values(TrafficSourceCodeEnum)
const defaultDateRange = (): GlobalDateRange => {
  const { dateFrom, dateTo } = getLastThirtyDaysRange()
  return { startDate: dateFrom, endDate: dateTo }
}
// The date picker clears to `{ startDate: '', endDate: '' }`, not `undefined`.
const isFilledRange = (range: GlobalDateRange | undefined): range is GlobalDateRange =>
  !!range?.startDate && !!range?.endDate
const isValidDateRange = (value: unknown): value is GlobalDateRange =>
  !!value &&
  typeof value === 'object' &&
  typeof (value as GlobalDateRange).startDate === 'string' &&
  typeof (value as GlobalDateRange).endDate === 'string' &&
  isFilledRange(value as GlobalDateRange)
const loadSavedFilter = (): SavedGlobalFilter => {
  const stored = localStorageService.getItem<SavedGlobalFilter>(STORAGE_KEY)
  if (!stored) {
    return {}
  }
  return {
    trafficSource: TRAFFIC_SOURCE_VALUES.includes(stored.trafficSource)
      ? stored.trafficSource
      : undefined,
    dateRange: isValidDateRange(stored.dateRange) ? stored.dateRange : undefined,
  }
}
export const useGlobalFilterStore = defineStore('global-filter', () => {
  const savedFilter = loadSavedFilter()
  const trafficSource = ref<TrafficSourceCodeEnum | undefined>(savedFilter.trafficSource)
  const dateRange = ref<GlobalDateRange | undefined>(savedFilter.dateRange ?? defaultDateRange())
  const saveFilter = () => {
    localStorageService.setItem<SavedGlobalFilter>(STORAGE_KEY, {
      trafficSource: trafficSource.value,
      dateRange: dateRange.value,
    })
  }
  const setTrafficSource = (value: TrafficSourceCodeEnum | undefined) => {
    trafficSource.value = value
    saveFilter()
  }
  const setDateRange = (range: GlobalDateRange | undefined) => {
    dateRange.value = isFilledRange(range) ? range : undefined
    saveFilter()
  }
  const ensureDateRange = () => {
    if (!isFilledRange(dateRange.value)) {
      setDateRange(defaultDateRange())
    }
  }
  return { trafficSource, dateRange, setTrafficSource, setDateRange, ensureDateRange }
})
